/**
 * Adapter d'impression via Hardware Bridge local
 * Communique directement avec le bridge local sur localhost:3334 (HTTPS)
 * Fonctionne avec Safari et tous les navigateurs (pas besoin de WebUSB)
 */

import {
  PrinterAdapter,
  PrintJob,
  PrinterStatus,
  PrinterError,
} from './PrinterAdapter';
import { logger } from '../../utils/logger';
import { enTetesBridgeJson } from '../bridgeHeaders';

const HARDWARE_BRIDGE_URL = 'https://localhost:3334';

interface HardwareBridgeHealth {
  status: string;
  posId: string;
  hardware: {
    scale: {
      enabled: boolean;
      connected: boolean;
    };
    epson: {
      enabled: boolean;
      queue: string;
      available: boolean;
    };
    zebra: {
      enabled: boolean;
      queue: string;
      available: boolean;
      /** Branchée en USB sur CE poste (bridge du 20/10/2026 ; null = lecture impossible). */
      connected?: boolean | null;
    };
    /** Imprimante A4 réseau (EPSON ET-3950) — absente des bridges d'avant le 19/10/2026. */
    a4?: {
      enabled: boolean;
      queue: string;
      available: boolean;
    };
  };
  cupsPrinters: string[];
}

/**
 * Vérifier si le Hardware Bridge local est disponible
 */
export async function isHardwareBridgeAvailable(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    const response = await fetch(`${HARDWARE_BRIDGE_URL}/health`, {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const health = await response.json() as HardwareBridgeHealth;
      logger.log('[HardwareBridge] Détecté:', health.posId);
      return true;
    }
    return false;
  } catch (error) {
    // Bridge non disponible (timeout ou connexion refusée)
    return false;
  }
}

/**
 * Obtenir le statut complet du Hardware Bridge
 */
export async function getHardwareBridgeStatus(): Promise<HardwareBridgeHealth | null> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const response = await fetch(`${HARDWARE_BRIDGE_URL}/health`, {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Adapter EPSON via Hardware Bridge local
 */
export class HardwareBridgeEpsonAdapter implements PrinterAdapter {
  readonly modelName = 'EPSON (Hardware Bridge)';
  readonly connectionType = 'LOCAL_BRIDGE' as const;

  private connected = false;
  private bridgeUrl = HARDWARE_BRIDGE_URL;

  /**
   * Connecter à l'imprimante via le bridge local
   */
  async connect(): Promise<void> {
    try {
      logger.log('[HardwareBridgeEpson] Vérification du bridge local...');

      const health = await getHardwareBridgeStatus();

      if (!health) {
        throw new PrinterError(
          'Hardware Bridge non disponible. Vérifiez que le bridge est démarré sur ce poste.',
          'CONNECTION_ERROR'
        );
      }

      if (!health.hardware.epson.enabled) {
        throw new PrinterError(
          'Imprimante EPSON désactivée dans la configuration du bridge.',
          'CONFIG_NOT_FOUND'
        );
      }

      if (!health.hardware.epson.available) {
        throw new PrinterError(
          `Queue CUPS "${health.hardware.epson.queue}" non trouvée. Vérifiez la configuration CUPS.`,
          'PRINTER_NOT_FOUND'
        );
      }

      this.connected = true;
      logger.log(
        '[HardwareBridgeEpson] Connecté via',
        health.posId,
        '- Queue:',
        health.hardware.epson.queue
      );
    } catch (error: unknown) {
      logger.error('[HardwareBridgeEpson] Erreur connexion:', error);
      if (error instanceof PrinterError) throw error;
      throw new PrinterError(
        `Échec connexion: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        'CONNECTION_ERROR',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Déconnecter l'imprimante
   */
  async disconnect(): Promise<void> {
    this.connected = false;
    logger.log('[HardwareBridgeEpson] Déconnecté');
  }

  /**
   * Vérifier si connecté
   */
  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Obtenir le statut de l'imprimante
   */
  async getStatus(): Promise<PrinterStatus> {
    try {
      const health = await getHardwareBridgeStatus();

      if (!health) {
        return {
          connected: false,
          online: false,
          paperStatus: 'unknown',
          error: 'Hardware Bridge non disponible',
        };
      }

      return {
        connected: health.hardware.epson.available,
        online: health.hardware.epson.available,
        paperStatus: health.hardware.epson.available ? 'ok' : 'unknown',
      };
    } catch (error) {
      return {
        connected: false,
        online: false,
        paperStatus: 'unknown',
        error: 'Impossible de vérifier le statut',
      };
    }
  }

  /**
   * Imprimer un ticket
   */
  async print(job: PrintJob): Promise<void> {
    if (!this.isConnected()) {
      await this.connect();
    }

    try {
      logger.log(
        '[HardwareBridgeEpson] Envoi impression:',
        job.metadata?.saleNumber
      );

      const response = await fetch(`${this.bridgeUrl}/epson/print`, {
        method: 'POST',
        headers: enTetesBridgeJson(),
        body: JSON.stringify({
          lines: job.lines,
          metadata: job.metadata,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message);
      }

      logger.log('[HardwareBridgeEpson] Impression réussie:', result.message);
    } catch (error: unknown) {
      logger.error('[HardwareBridgeEpson] Erreur impression:', error);
      throw new PrinterError(
        `Échec impression: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        'PRINT_ERROR',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Ouvrir le tiroir-caisse
   */
  async openCashDrawer(): Promise<void> {
    try {
      logger.log('[HardwareBridgeEpson] Ouverture tiroir-caisse...');

      const response = await fetch(`${this.bridgeUrl}/epson/drawer`, {
        method: 'POST',
        headers: enTetesBridgeJson(),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message);
      }

      logger.log('[HardwareBridgeEpson] Tiroir-caisse ouvert');
    } catch (error: unknown) {
      logger.error('[HardwareBridgeEpson] Erreur tiroir-caisse:', error);
      throw new PrinterError(
        `Échec ouverture tiroir: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        'PRINT_ERROR',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Test d'impression simple
   */
  async testPrint(): Promise<void> {
    try {
      logger.log('[HardwareBridgeEpson] Test d\'impression...');

      const response = await fetch(`${this.bridgeUrl}/epson/test`, {
        method: 'POST',
        headers: enTetesBridgeJson(),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message);
      }

      logger.log('[HardwareBridgeEpson] Test réussi:', result.message);
    } catch (error: unknown) {
      logger.error('[HardwareBridgeEpson] Erreur test:', error);
      throw new PrinterError(
        `Échec test impression: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        'PRINT_ERROR',
        error instanceof Error ? error : undefined
      );
    }
  }
}

/**
 * Service ZEBRA via Hardware Bridge local
 */
export class HardwareBridgeZebraService {
  private bridgeUrl = HARDWARE_BRIDGE_URL;

  /**
   * Vérifier si l'imprimante ZEBRA est disponible
   */
  async isAvailable(): Promise<boolean> {
    try {
      const health = await getHardwareBridgeStatus();
      return health?.hardware.zebra.available ?? false;
    } catch {
      return false;
    }
  }

  /**
   * Imprimer une étiquette ZPL
   */
  async printZPL(zpl: string): Promise<{ success: boolean; message: string }> {
    try {
      logger.log('[HardwareBridgeZebra] Envoi impression ZPL...');

      const response = await fetch(`${this.bridgeUrl}/zebra/print`, {
        method: 'POST',
        headers: enTetesBridgeJson(),
        body: JSON.stringify({ zpl }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message);
      }

      logger.log('[HardwareBridgeZebra] Impression réussie:', result.message);
      return result;
    } catch (error: unknown) {
      logger.error('[HardwareBridgeZebra] Erreur impression:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue',
      };
    }
  }

  /**
   * Test d'impression
   */
  async testPrint(): Promise<{ success: boolean; message: string }> {
    try {
      logger.log('[HardwareBridgeZebra] Test d\'impression...');

      const response = await fetch(`${this.bridgeUrl}/zebra/test`, {
        method: 'POST',
        headers: enTetesBridgeJson(),
      });

      const result = await response.json();

      logger.log('[HardwareBridgeZebra] Test:', result.success ? 'réussi' : 'échoué');
      return result;
    } catch (error: unknown) {
      logger.error('[HardwareBridgeZebra] Erreur test:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue',
      };
    }
  }
}

// Singleton pour le service ZEBRA
export const hardwareBridgeZebra = new HardwareBridgeZebraService();

/**
 * Imprimante A4 réseau (EPSON EcoTank ET-3950), via le bridge du poste (19/10/2026).
 * Sert d'abord au rapport Z : sur l'écran tactile de la caisse, ouvrir le PDF
 * puis passer par le menu Imprimer de Safari était impraticable.
 */
export class HardwareBridgeA4Service {
  private bridgeUrl = HARDWARE_BRIDGE_URL;

  /** Le bridge de CE poste connaît-il l'imprimante A4 ? */
  async isAvailable(): Promise<boolean> {
    try {
      const health = await getHardwareBridgeStatus();
      return health?.hardware.a4?.available ?? false;
    } catch {
      return false;
    }
  }

  async printPdf(pdf: Blob, titre: string): Promise<{ success: boolean; message: string }> {
    try {
      const octets = new Uint8Array(await pdf.arrayBuffer());
      let binaire = '';
      for (let i = 0; i < octets.length; i += 0x8000) {
        binaire += String.fromCharCode(...octets.subarray(i, i + 0x8000));
      }
      const response = await fetch(`${this.bridgeUrl}/a4/print`, {
        method: 'POST',
        headers: enTetesBridgeJson(),
        body: JSON.stringify({ pdf_base64: btoa(binaire), titre }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      return result;
    } catch (error: unknown) {
      logger.error('[HardwareBridgeA4] Erreur impression:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue',
      };
    }
  }
}

export const hardwareBridgeA4 = new HardwareBridgeA4Service();

/**
 * La Zebra passe d'une caisse à l'autre : si le bridge de CE poste dit qu'elle
 * n'y est pas branchée, on le dit tel quel plutôt que de tenter une autre voie
 * (l'API d'impression réseau) qui échouerait avec un message obscur.
 */
export function zebraAbsenteIci(health: HardwareBridgeHealth | null | undefined): string | null {
  if (health?.hardware?.zebra?.connected === false) {
    return `La Zebra n'est pas branchée sur ce poste (${health.posId || 'caisse'}). Branchez-la ici, ou imprimez depuis la caisse où elle se trouve.`;
  }
  return null;
}
